import { useEffect } from 'react';
import styled from 'styled-components';
import themeGet from '@styled-system/theme-get';
import ReactTooltip from 'react-tooltip';
import { useTranslation } from 'react-i18next';

import Select from '../../../../shared/Select/Select.jsx';
import Text from '../../../../shared/Text';
import ToggleSwitch from '../../../../shared/FormStyles/ToggleSwitch.jsx';
import Tooltip from '../../../../shared/Tooltip/Tooltip.jsx';

import {
  Section,
  SectionBody,
  Container,
  ToggleContainer,
  ToggleLabel,
} from './StyledComponents';

import { EMAIL, SMS } from '../../utils/constants';
import { MessageTextInput } from '../Posts/MessageTextInput.jsx';
import SmsTextField from '../../../../shared/SmsTextField/SmsTextField.jsx';
import { makeGetRecipientsCount } from './helpers';
import Button from '../../../../shared/Button';
import { useCampaignContext } from '../../contexts/CampaignContext.jsx';

export const MessageForm = ({
  message,
  values,
  handleChange,
  setFieldValue,
  currentPreview,
  setCurrentPreview,
  onSendTest,
  audiences = [],
}) => {
  const { t } = useTranslation('posts');
  const { campaign } = useCampaignContext();
  const isReadOnly = message.sent || !!campaign?.deletedAt;

  const getRecipientsCount = makeGetRecipientsCount(values, message);

  const handleSmsChange = () => {
    if (currentPreview === SMS) setCurrentPreview(EMAIL);
    setFieldValue('sendSms', !values.sendSms);
  };

  const messageTemplateOptions = [
    { value: 'with_tiles', label: 'Tiles with Images' },
    { value: 'only_text', label: 'Plain text with CTA' },
  ];

  const audiencesOptions = [
    { value: 'all', label: 'All Audiences' },
    { value: '', label: 'No Audience' },
    ...audiences.map(audience => ({
      value: audience.id,
      label: audience.name,
    })),
  ];

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <Container>
      <Tooltip />

      <Section>
        <Text padding="14px 24px" as="div" variant="h6">
          Set Audience
        </Text>
        <SectionBody>
          <LabelContainer>
            <SelectLabel htmlFor="audienceId">
              {t('reviewModal.messageForm.audiencePreview')}
            </SelectLabel>
            <Select
              components={{
                IndicatorSeparator: () => null,
              }}
              onChange={option => setFieldValue('audienceId', option.value)}
              options={audiencesOptions}
              value={audiencesOptions.find(
                item => item.value === values.audienceId,
              )}
              isSearchable={false}
              name="audienceId"
              id="audiendeId"
              isDisabled={isReadOnly}
            />
          </LabelContainer>
          <LabelContainer>
            <SelectLabel htmlFor="targetedUsers">
              {t('reviewModal.messageForm.recipients')}
            </SelectLabel>
            <Select
              components={{
                IndicatorSeparator: () => null,
              }}
              isSearchable={false}
              onChange={option => setFieldValue('targetedUsers', option.value)}
              options={message.sendToOptions}
              value={message.sendToOptions.find(
                item => item.value === values.targetedUsers,
              )}
              name="targetedUsers"
              isDisabled={isReadOnly}
              id="targetedUsers"
            />
          </LabelContainer>
          <RecipientsHeader>
            <div>
              <Text variant="body3" color="black.80" marginRight="8px">
                {t('reviewModal.messageForm.emailRecipients')}
              </Text>
              <Text variant="button">{getRecipientsCount(EMAIL)}</Text>
            </div>
            <div>
              <Text variant="body3" color="black.80" marginRight="8px">
                {t('reviewModal.messageForm.smsRecipients')}
              </Text>
              <Text variant="button">{getRecipientsCount(SMS)}</Text>
            </div>
          </RecipientsHeader>
        </SectionBody>
      </Section>

      <Section>
        <Text padding="14px 24px" as="div" variant="h6">
          Create Email
        </Text>
        <SectionBody>
          <LabelContainer>
            <SelectLabel htmlFor="messageTemplate">
              {t('reviewModal.messageForm.emailTemplate')}
            </SelectLabel>
            <Select
              components={{
                IndicatorSeparator: () => null,
              }}
              isSearchable={false}
              onChange={option =>
                setFieldValue('messageTemplate', option.value)
              }
              options={messageTemplateOptions}
              value={messageTemplateOptions.find(
                item => item.value === values.messageTemplate,
              )}
              name="messageTemplate"
              isDisabled={isReadOnly}
              id="messageTemplate"
            />
          </LabelContainer>
          <LabelContainer>
            <MessageTextInput
              onChange={handleChange}
              value={values.subject}
              placeholder={t('reviewModal.messageForm.emptySubjectDefault')}
              name="subject"
              isReminderTab={isReadOnly}
              charactersLimit={80}
              label={t('reviewModal.messageForm.subjectLabel')}
              labelVariant="subtitle2"
              color="black.60"
            />
          </LabelContainer>
          {values.messageTemplate !== 'only_text' && (
            <LabelContainer>
              <MessageTextInput
                onChange={handleChange}
                value={values.headline}
                placeholder={t('reviewModal.messageForm.emptyHeadlineDefault')}
                name="headline"
                isReminderTab={isReadOnly}
                charactersLimit={80}
                label={t('reviewModal.messageForm.headlineLabel')}
                labelVariant="subtitle2"
                color="black.60"
              />
            </LabelContainer>
          )}
          <LabelContainer>
            <MessageTextInput
              onChange={handleChange}
              value={values.message}
              isReminderTab={isReadOnly}
              placeholder={t('reviewModal.messageForm.messagePlaceholder')}
              name="message"
              as="textarea"
              label={t('reviewModal.messageForm.messageLabel')}
              labelVariant="subtitle2"
              color="black.60"
            />
          </LabelContainer>
          {values.messageTemplate === 'only_text' && (
            <>
              <LabelContainer>
                <MessageTextInput
                  onChange={handleChange}
                  value={values.messageCta}
                  placeholder={t('reviewModal.messageForm.defaultCta')}
                  name="messageCta"
                  charactersLimit={80}
                  label={'Button Text'}
                  labelVariant="subtitle2"
                  color="black.60"
                  isReminderTab={isReadOnly}
                />
              </LabelContainer>
              <LabelContainer>
                <MessageTextInput
                  onChange={handleChange}
                  value={values.messageFooter}
                  placeholder={'Message Footer'}
                  name="messageFooter"
                  as="textarea"
                  label={'Footer Message'}
                  labelVariant="subtitle2"
                  color="black.60"
                  isReminderTab={isReadOnly}
                />
              </LabelContainer>
            </>
          )}

          <ToggleContainer>
            <ToggleSwitch
              id="sendSms"
              name="sendSms"
              onChange={() => handleSmsChange()}
              value={values.sendSms}
              disabled={isReadOnly}
              label="Include Text Messages"
              invertLabelPosition
            />
            <ToggleLabel
              isReminderTab={false}
              data-tip={t('reviewModal.messageForm.textMsgIcon')}
            >
              <i className="fa-regular fa-circle-info"></i>
            </ToggleLabel>
          </ToggleContainer>
          {values.sendSms && (
            <LabelContainer>
              <SmsTextField
                name="smsText"
                value={values.smsText}
                onChange={text => setFieldValue('smsText', text)}
                label={t('reviewModal.messageForm.smsTextLabel')}
                placeholder={t('reviewModal.messageForm.smsTextPlaceholder')}
                labelVariant="subtitle2"
                color="black.60"
                disabled={isReadOnly}
                disabledVariant="whiteBg"
              />
            </LabelContainer>
          )}
          {!isReadOnly && (
            <Button
              onClick={() => onSendTest()}
              mode="outline"
              variant="black"
              width="100%"
              height="40px"
            >
              Send Test Message To Myself
            </Button>
          )}
        </SectionBody>
      </Section>
    </Container>
  );
};

const RecipientsHeader = styled.div`
  padding: 10px;
  display: flex;
  justify-content: space-between;
  background-color: ${themeGet('colors.white')};
  border-radius: 8px;
  border: 1px solid ${themeGet('colors.black.10')};
`;

const LabelContainer = styled.div`
  margin-bottom: 24px;

  &:nth-child(2) {
    margin-bottom: 12px;
  }
`;

// Plain attrs rather than defaultAttrs: Text's own color default is applied
// first, and SelectLabel's color has to win over it (nobody passes these).
const SelectLabel = styled(Text).attrs({
  variant: 'subtitle2',
  color: 'black.60',
  as: 'label',
})`
  margin-bottom: 8px;
  text-align: left;
  cursor: ${props => (props.link ? 'pointer' : 'auto')};
`;
